import { apiClient } from '../api/client';
import { getPreviewRedirectOrigin } from '../utils/oauth';
import type {
  PlatformRuntime,
  RuntimeAuthAdapter,
  RuntimeFallbackActivatePayload,
  RuntimeFallbackLoginPayload,
  RuntimeLocationSnapshot,
  RuntimeNavigate,
  RuntimeNavigationAdapter,
  RuntimeSearchAdapter,
  RuntimeSessionAdapter,
  RuntimeToastsAdapter,
  RuntimeTokenResponse,
  RuntimeUser,
} from './contracts';

const AUTH_TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const TOAST_AUTO_DISMISS_MS = 3000;
const HOST_TOAST_EVENT = 'm360:runtime-toast';

interface RuntimeHttpClient {
  get: <T>(url: string) => Promise<{ data: T }>;
  post: <T>(url: string, payload?: unknown) => Promise<{ data: T }>;
}

export function createHostSessionAdapter(storage: Storage = sessionStorage): RuntimeSessionAdapter {
  return {
    getAuthToken: () => storage.getItem(AUTH_TOKEN_KEY),
    setAuthToken: (token: string) => {
      storage.setItem(AUTH_TOKEN_KEY, token);
    },
    clearAuthToken: () => {
      storage.removeItem(AUTH_TOKEN_KEY);
    },
    getRefreshToken: () => storage.getItem(REFRESH_TOKEN_KEY),
    setRefreshToken: (token: string) => {
      storage.setItem(REFRESH_TOKEN_KEY, token);
    },
    clearRefreshToken: () => {
      storage.removeItem(REFRESH_TOKEN_KEY);
    },
  };
}

export function createHostAuthAdapter(
  session: RuntimeSessionAdapter,
  httpClient: RuntimeHttpClient = apiClient
): RuntimeAuthAdapter {
  return {
    getCurrentUser: async () => {
      const response = await httpClient.get<RuntimeUser>('/auth/me');
      return response.data;
    },
    logout: async () => {
      await httpClient.post('/auth/logout');
    },
    subscribeToOAuthHandoff: (onAccessToken) => {
      const previewRedirectOrigin = getPreviewRedirectOrigin();
      if (!previewRedirectOrigin) {
        return () => undefined;
      }

      const handleOAuthHandoff = (event: MessageEvent) => {
        if (event.origin !== previewRedirectOrigin) {
          return;
        }

        if (!event.data || typeof event.data !== 'object') {
          return;
        }

        const payload = event.data as {
          type?: string;
          accessToken?: string;
          refreshToken?: string;
        };
        if (payload.type !== 'oauth-handoff' || typeof payload.accessToken !== 'string') {
          return;
        }

        try {
          session.setAuthToken(payload.accessToken);
          if (typeof payload.refreshToken === 'string') {
            session.setRefreshToken(payload.refreshToken);
          }
        } catch (storageError) {
          console.error('[RuntimeAuth] Failed to store preview auth token', storageError);
          return;
        }

        onAccessToken(payload.accessToken);
      };

      window.addEventListener('message', handleOAuthHandoff);
      return () => window.removeEventListener('message', handleOAuthHandoff);
    },
    redirectToSignedOut: () => {
      window.location.href = '/';
    },
    loginWithFallback: async (
      payload: RuntimeFallbackLoginPayload
    ): Promise<RuntimeTokenResponse> => {
      const response = await httpClient.post<RuntimeTokenResponse>('/auth/fallback/login', payload);
      session.setAuthToken(response.data.accessToken);
      if (response.data.refreshToken) {
        session.setRefreshToken(response.data.refreshToken);
      }
      return response.data;
    },
    activateWithFallback: async (
      payload: RuntimeFallbackActivatePayload
    ): Promise<RuntimeTokenResponse> => {
      const response = await httpClient.post<RuntimeTokenResponse>(
        '/auth/fallback/activate',
        payload
      );
      session.setAuthToken(response.data.accessToken);
      if (response.data.refreshToken) {
        session.setRefreshToken(response.data.refreshToken);
      }
      return response.data;
    },
  };
}

export function createHostNavigationAdapter(
  navigate: RuntimeNavigate,
  location: RuntimeLocationSnapshot
): RuntimeNavigationAdapter {
  return {
    navigate,
    currentPath: () => location.pathname,
    currentSearch: () => location.search,
  };
}

export function createHostSearchAdapter(navigate: RuntimeNavigate): RuntimeSearchAdapter {
  return {
    submitGlobalSearch: (query: string) => {
      navigate(`/products?search=${encodeURIComponent(query)}`);
    },
    openSearchResult: ({ id }) => {
      navigate(`/products/${id}`);
    },
  };
}

export function createHostToastsAdapter(): RuntimeToastsAdapter {
  return {
    autoDismissMs: TOAST_AUTO_DISMISS_MS,
    createToastId: () => Math.random().toString(36).substring(2, 9),
    publishToast: (toast) => {
      window.dispatchEvent(
        new CustomEvent(HOST_TOAST_EVENT, {
          detail: toast,
        })
      );
    },
    subscribeToToasts: (listener) => {
      const handleToastEvent = (event: Event) => {
        if (!(event instanceof CustomEvent)) {
          return;
        }

        const detail = event.detail;
        if (!detail || typeof detail !== 'object') {
          return;
        }

        const payload = detail as { message?: string; type?: string };
        if (typeof payload.message !== 'string') {
          return;
        }

        if (
          payload.type !== 'success' &&
          payload.type !== 'error' &&
          payload.type !== 'info' &&
          payload.type !== 'warning'
        ) {
          return;
        }

        listener({
          message: payload.message,
          type: payload.type,
        });
      };

      window.addEventListener(HOST_TOAST_EVENT, handleToastEvent);
      return () => {
        window.removeEventListener(HOST_TOAST_EVENT, handleToastEvent);
      };
    },
  };
}

export function createDefaultPlatformRuntime(): PlatformRuntime {
  const session = createHostSessionAdapter();
  return {
    version: '2.0.0',
    session,
    auth: createHostAuthAdapter(session),
    navigation: {
      createAdapter: createHostNavigationAdapter,
    },
    search: {
      createAdapter: createHostSearchAdapter,
    },
    toasts: createHostToastsAdapter(),
  };
}
